import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useToolsAuth } from "@/context/ToolsAuthContext";
import { toolsApi, formatApiError, goToCheckout } from "@/lib/toolsApi";
import { ToolsPaywall } from "@/components/tools/ToolsPaywall";
import { Workbook } from "@/components/tools/Workbook";

const UpgradePanel = () => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const upgrade = async (lookupKey) => {
    setBusy(true);
    try {
      const { data } = await toolsApi.post("/tools/upgrade", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      goToCheckout(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid="upgrade-panel">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="upgrade-open"
        className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-4 py-2 text-xs font-medium tracking-wide hover:bg-wine transition-colors"
      >
        <Sparkles size={14} /> Upgrade to membership
      </button>
      {open && (
        <div className="absolute right-0 mt-3 w-72 rounded-xl border border-gold/40 bg-cream shadow-xl p-5 z-30">
          <p className="text-sm text-charcoal/80 font-light">
            Your <span className="font-medium">$29</span> is credited toward your membership — so your first months are on us.
          </p>
          <button
            onClick={() => upgrade("tools_monthly")}
            disabled={busy}
            data-testid="upgrade-monthly"
            className="mt-4 w-full rounded-full bg-terracotta text-cream px-4 py-2.5 text-sm hover:bg-wine transition-colors disabled:opacity-60"
          >
            Monthly — $5/mo
          </button>
          <button
            onClick={() => upgrade("tools_yearly")}
            disabled={busy}
            data-testid="upgrade-yearly"
            className="mt-2 w-full rounded-full border border-terracotta text-terracotta px-4 py-2.5 text-sm hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-60"
          >
            Yearly — $45/yr
          </button>
        </div>
      )}
    </div>
  );
};

export default function Tools() {
  const { user, loading, logout } = useToolsAuth();

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream" data-testid="tools-loading">
        <Loader2 className="animate-spin text-terracotta" size={28} />
      </main>
    );
  }

  // Everything in the app is locked until purchase — non-payers (and logged-out
  // visitors) see the paywall with locked previews + pricing.
  if (!user || !user.has_access) {
    return <ToolsPaywall />;
  }

  const planLabel = user.plan === "subscription" ? "Member" : "Lifetime";

  return (
    <main className="min-h-screen bg-cream pt-28 pb-20 px-4 md:px-6" data-testid="tools-unlocked">
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap items-center justify-between gap-4 mb-8 border-b border-gold/30 pb-6"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-terracotta flex items-center gap-2">
              <span className="rounded-full bg-blush/60 text-wine px-2.5 py-0.5">{planLabel}</span>
            </p>
            <p className="mt-2 text-sm text-charcoal/60" data-testid="tools-account-email">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {user.can_upgrade && <UpgradePanel />}
            <button
              onClick={logout}
              data-testid="tools-logout"
              className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-wine transition-colors"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </motion.div>

        <Workbook />
      </div>
    </main>
  );
}
